/**
 * One order for every list, chosen by the reader rather than by whoever wrote
 * the page (issue #74).
 *
 * Nothing in here is React. A page declares its keys as data, this decides what
 * the URL means and what order the rows come out in, and the tests below pin
 * both. The alternative, which the console had, is `rows.sort(...)` inline on
 * seven pages, five of which then disagree about what "recent" means.
 *
 * Two rules are load-bearing rather than stylistic:
 *
 *   - **Every comparison ends in a tiebreak on the row's id.** `Array.sort` is
 *     stable, but the input is not: these lists are re-derived on every event the
 *     hub streams, so two rows with the same timestamp would swap places
 *     whenever the query refetched. A reader watching a list reorder itself
 *     under the cursor has no way to know nothing changed.
 *   - **The default is absent from the URL.** A link to a list is a link to that
 *     list, not to the state of a control the reader never touched, and it means
 *     a default can be changed later without breaking every link already
 *     written down.
 */

export type SortDirection = 'asc' | 'desc';

/** One thing a list can be ordered by. */
export interface SortOption<T> {
  /** What the URL carries. Stable: it appears in links people keep. */
  value: string;
  /** What the menu shows. */
  label: string;
  /**
   * Ascending: negative when `a` comes first. The direction is applied on top,
   * so a comparator never knows which way it is being read.
   */
  compare: (a: T, b: T) => number;
  /**
   * Which direction reads as "the interesting end first" for this key, used the
   * first time it is picked. A time is newest first; a name is A to Z. Without
   * this every key would open backwards half the time.
   */
  natural?: SortDirection;
}

export interface SortState {
  key: string;
  direction: SortDirection;
}

/** What a sortable route keeps in its search params. */
export interface SortSearch {
  sort?: string;
  dir?: SortDirection;
}

function naturalOf<T>(option: SortOption<T> | undefined): SortDirection {
  return option?.natural ?? 'asc';
}

/** The first option is the page's default, so a page's order is its own decision. */
export function defaultSort<T>(options: readonly SortOption<T>[]): SortState {
  const first = options[0];
  return { key: first?.value ?? '', direction: naturalOf(first) };
}

/**
 * What the URL is asking for, or the default.
 *
 * An unknown key falls back rather than throwing: the URL is user input, a key
 * can be retired, and a stale link should open the page it names rather than an
 * error boundary.
 */
export function readSort<T>(
  options: readonly SortOption<T>[],
  search: SortSearch | undefined,
): SortState {
  const fallback = defaultSort(options);
  const asked = options.find((option) => option.value === search?.sort);
  if (!asked) return fallback;
  const direction = search?.dir === 'asc' || search?.dir === 'desc' ? search.dir : naturalOf(asked);
  return { key: asked.value, direction };
}

/**
 * What to put in the URL for a state, with the default left out (see the note at
 * the top of this file).
 *
 * `undefined` rather than a missing property, because the router's search
 * updater is a spread over the previous search: a key has to be present and
 * undefined to clear one that is already there.
 */
export interface SortSearchPatch {
  sort: string | undefined;
  dir: SortDirection | undefined;
}

export function sortToSearch<T>(
  options: readonly SortOption<T>[],
  state: SortState,
): SortSearchPatch {
  const fallback = defaultSort(options);
  const isDefault = state.key === fallback.key && state.direction === fallback.direction;
  if (isDefault) return { sort: undefined, dir: undefined };
  const option = options.find((entry) => entry.value === state.key);
  return {
    sort: state.key,
    dir: state.direction === naturalOf(option) ? undefined : state.direction,
  };
}

/**
 * Picking the key already in use reverses it; picking another opens it at its
 * natural end. This is the behaviour of every sortable column anybody has used,
 * and it means one control does both jobs.
 */
export function pickSort<T>(
  options: readonly SortOption<T>[],
  state: SortState,
  value: string,
): SortState {
  const option = options.find((entry) => entry.value === value);
  if (!option) return state;
  if (option.value !== state.key) return { key: option.value, direction: naturalOf(option) };
  return { key: state.key, direction: state.direction === 'asc' ? 'desc' : 'asc' };
}

/**
 * The comparison a sorted list actually runs: the chosen key, in the chosen
 * direction, with the id as the last word so equal rows hold still.
 */
export function sortComparator<T>(
  options: readonly SortOption<T>[],
  state: SortState,
  id: (row: T) => string,
): (a: T, b: T) => number {
  const option = options.find((entry) => entry.value === state.key) ?? options[0];
  const sign = state.direction === 'desc' ? -1 : 1;
  return (a, b) => {
    const by = option ? option.compare(a, b) * sign : 0;
    if (by !== 0) return by;
    // The tiebreak is not reversed: it is there to be deterministic, not to be
    // meaningful, and reversing it would make a reversal reorder equal rows.
    return id(a).localeCompare(id(b));
  };
}

/** A new array, ordered. Never sorts the caller's array in place: these are query results. */
export function applySort<T>(
  rows: readonly T[],
  options: readonly SortOption<T>[],
  state: SortState,
  id: (row: T) => string,
): T[] {
  return [...rows].sort(sortComparator(options, state, id));
}

/**
 * Comparators for the shapes every page sorts by, so "recent" cannot mean two
 * things on two pages.
 *
 * A missing date sorts as the oldest possible rather than as now: a task with no
 * `startedAt` has not started, and putting it at the top of "most recently
 * started" would be a lie the reader has no way to check.
 */
export function byTime<T>(at: (row: T) => string | null | undefined): (a: T, b: T) => number {
  return (a, b) => {
    const left = Date.parse(at(a) ?? '');
    const right = Date.parse(at(b) ?? '');
    return (Number.isNaN(left) ? -Infinity : left) - (Number.isNaN(right) ? -Infinity : right);
  };
}

/** Case-insensitive and locale-aware, so `Zack` and `alice` sort as a person expects. */
export function byText<T>(text: (row: T) => string | null | undefined): (a: T, b: T) => number {
  return (a, b) => (text(a) ?? '').localeCompare(text(b) ?? '', undefined, { sensitivity: 'base' });
}

export function byNumber<T>(value: (row: T) => number | null | undefined): (a: T, b: T) => number {
  return (a, b) => (value(a) ?? 0) - (value(b) ?? 0);
}

/**
 * For the keys whose order is neither alphabetical nor numeric: a size is
 * `tiny` to `xl`, a state runs from queued to done. The page passes the order it
 * means, and anything outside the list sorts last rather than first, since an
 * unknown value is not more interesting than a known one.
 */
export function byRank<T>(
  rank: readonly string[],
  value: (row: T) => string | null | undefined,
): (a: T, b: T) => number {
  const place = (row: T) => {
    const found = rank.indexOf(value(row) ?? '');
    return found === -1 ? rank.length : found;
  };
  return (a, b) => place(a) - place(b);
}
